import { create } from 'zustand';
import { api, setToken, onUnauthorized, type User, type Tenant } from '../lib/api';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  status: 'loading' | 'signed-in' | 'signed-out';
  error: string | null;

  restore: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    workshopName: string;
    name: string;
    email: string;
    password: string;
    position?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  clearError: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  tenant: null,
  status: 'loading',
  error: null,

  async restore() {
    try {
      const { user, tenant } = await api.get<{ user: User; tenant: Tenant }>('/auth/me');
      set({ user, tenant, status: 'signed-in', error: null });
    } catch {
      set({ user: null, tenant: null, status: 'signed-out' });
    }
  },

  async login(email, password) {
    set({ error: null });
    const { user, token } = await api.post<{ user: User; token: string }>('/auth/login', {
      email,
      password,
    });
    setToken(token);
    set({ user, status: 'signed-in' });
    await get().refreshTenant();
  },

  async register(input) {
    set({ error: null });
    const { user, token } = await api.post<{ user: User; token: string }>('/auth/register', input);
    setToken(token);
    set({ user, status: 'signed-in' });
    await get().refreshTenant();
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      /* signing out locally matters more than the round trip */
    }
    setToken(null);
    set({ user: null, tenant: null, status: 'signed-out' });
  },

  async refreshTenant() {
    const { user, tenant } = await api.get<{ user: User; tenant: Tenant }>('/auth/me');
    set({ user, tenant });
  },

  clearError: () => set({ error: null }),
}));

// A rejected session anywhere in the app drops us back to the sign-in screen.
onUnauthorized.add(() => {
  if (useAuth.getState().status === 'signed-in') {
    useAuth.setState({ user: null, tenant: null, status: 'signed-out' });
  }
});
